import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Send, Paperclip } from "lucide-react";

export function Chat({ orderId, testid = "chat" }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/portal/orders/${orderId}/messages`).then((r) => setMessages(r.data)).catch(() => {});
  }, [orderId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["application/pdf", "image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("Only PDF or image files");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("File must be under 4MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setAttachment(reader.result);
    reader.readAsDataURL(file);
  };

  const openAttachment = async (m) => {
    if (!m.attachment_url && m.attachment) {
      window.open(m.attachment, "_blank");
      return;
    }
    try {
      const res = await api.get(m.attachment_url, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !attachment) return;
    setSending(true);
    try {
      await api.post(`/portal/orders/${orderId}/messages`, { text: text.trim(), attachment });
      setText("");
      setAttachment("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="glass rounded-2xl flex flex-col h-[480px]" data-testid={testid}>
      <div className="px-5 py-3.5 border-b border-white/10">
        <p className="font-display font-semibold text-white text-sm">Project Chat</p>
        <p className="text-[10px] font-mono-tech tracking-widest text-slate-500 uppercase">Founder ↔ Specialist</p>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid={`${testid}-messages`}>
        {messages.length === 0 && (
          <p className="text-center text-xs text-slate-500 py-10">No messages yet. Start the conversation.</p>
        )}
        {messages.map((m) => {
          const isSystem = m.sender_role === "system";
          return (
            <div key={m.id} className={isSystem ? "flex justify-center" : "flex"}>
              <div
                className={
                  isSystem
                    ? "text-[10px] font-mono-tech text-slate-500 bg-white/5 rounded-full px-3.5 py-1.5 max-w-[90%] text-center"
                    : "max-w-[80%] glass rounded-xl px-4 py-2.5"
                }
              >
                {!isSystem && (
                  <p className="text-[10px] font-mono-tech tracking-wider text-[#FF8800] mb-1">
                    {m.sender_name} · {m.sender_role}
                  </p>
                )}
                {m.text && <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{m.text}</p>}
                {(m.attachment_url || m.attachment) && (
                  <button onClick={() => openAttachment(m)} className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-[#00B0FF] hover:underline">
                    <Paperclip className="w-3 h-3" /> Attachment
                  </button>
                )}
                <p className="text-[9px] text-slate-600 mt-1">
                  {new Date(m.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="p-3 border-t border-white/10 flex items-center gap-2">
        <label className="w-9 h-9 rounded-lg glass flex items-center justify-center text-slate-400 hover:text-[#FF8800] cursor-pointer shrink-0" data-testid={`${testid}-attach`}>
          <Paperclip className="w-4 h-4" />
          <input type="file" accept=".pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} />
        </label>
        <input
          data-testid={`${testid}-input`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={attachment ? "File attached — add a note..." : "Type a message..."}
          className="flex-1 bg-[#06080E] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-[#FF8800]"
        />
        <Button type="submit" size="icon" disabled={sending} data-testid={`${testid}-send`} className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] shrink-0">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}
